import { useMutation, useQuery, UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import { autoMarkAsDoneEndpoint, getAllInfoService, pauseBetweenAppointmentsEndpoint, pauseBetweenAppointmentsCmsEndpoint, locationTravelTimeEndpoint, subscriptionPackageEndpoint, updateStartOnlineReservation, updateEndOnlineReservation, viberStaffNotificationsEndpoint, viberEnabledEndpoint, showAppointmentPriceEndpoint, showAppointmentDurationEndpoint, onlineReservationDaysEndpoint, onlineReservationTimePermanentEndpoint, clientCancellationWindowHoursEndpoint } from "../endpoints";
import { toast } from "react-toastify";

export interface SubscriptionPackageInfo {
  subscriptionPackage: string;
  subscriptionDisplayName: string;
  subscriptionType: string;
  subscriptionBasePrice: number;
  subscriptionViberIncluded: boolean;
  subscriptionViberExtraPrice: number;
  subscriptionPrice: number;
  subscriptionNextPaymentDate: string;
  billingMonth: string;
}

export interface UpdateSubscriptionPackagePayload {
  subscriptionPackage: string;
  subscriptionDisplayName: string;
  subscriptionBasePrice: number;
  subscriptionViberIncluded: boolean;
  subscriptionViberExtraPrice?: number;
  subscriptionType?: string;
  subscriptionNextPaymentDate?: string;
}

export const useGetAllInfoService = (): UseQueryResult<any, unknown> => {
    const query = useQuery({
      queryKey: ["getAllServices"],
      queryFn: async () => {
        const response: AxiosResponse<any> = await httpClient.get(getAllInfoService());
        return response.data;
      },
      retry: 0,
    });
    return query;
  };

export const useGetSubscriptionPackage = (): UseQueryResult<SubscriptionPackageInfo, unknown> => {
  return useQuery({
    queryKey: ["subscriptionPackage"],
    queryFn: async () => {
      const response: AxiosResponse<SubscriptionPackageInfo> = await httpClient.get(subscriptionPackageEndpoint());
      return response.data;
    },
    retry: 0,
  });
};

export const useUpdateSubscriptionPackage = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, UpdateSubscriptionPackagePayload>({
    mutationFn: async (payload) => {
      const response: AxiosResponse<any> = await httpClient.post(subscriptionPackageEndpoint(), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPackage"] });
      queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
      toast.success("Pretplatni paket je spašen");
    },
    onError: () => {
      toast.error("Greška prilikom spremanja pretplatnog paketa");
    },
  });
};

export const useUpdateStartOnlineReservation = () => {
  const mutation = useMutation<any, Error, string>({
    mutationFn: async (onlineReservationStart: string) => {
      const response: AxiosResponse<any> = await httpClient.post(updateStartOnlineReservation(), { onlineReservationStart });
      return response.data;
    },
    onError: (error) => {
      toast.error('Neispravan unos vremena početka online rezervacije!');
    },
  });
  return mutation;
};

export const useUpdateEndOnlineReservation = () => {
  const mutation = useMutation<any, Error, string>({
    mutationFn: async (onlineReservationEnd: string) => {
      const response: AxiosResponse<any> = await httpClient.post(updateEndOnlineReservation(), { onlineReservationEnd });
      return response.data;
    },
    onError: (error) => {
      toast.error('Neispravan unos vremena kraja online rezervacije!');
    },
  });
  return mutation;
};

export const useAutoMarkAsDoneReservation = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (autoMarkAsDone: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(autoMarkAsDoneEndpoint(), { autoMarkAsDone });
      return response.data;
    },
    onError: (error) => {
      toast.error('Greška prilikom promjene opcije!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const usePauseBetweenAppointments = () => {
  const mutation = useMutation<any, Error, { pauseBetweenAppointments: boolean; pauseBetweenAppointmentsDuration: number }>({
    mutationFn: async ({ pauseBetweenAppointments, pauseBetweenAppointmentsDuration }) => {
      const response: AxiosResponse<any> = await httpClient.post(
        pauseBetweenAppointmentsEndpoint(), 
        { pauseBetweenAppointments, pauseBetweenAppointmentsDuration }
      );
      return response.data;
    },
    onError: (error) => {
      toast.error('Greška prilikom promjene opcije!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const usePauseBetweenAppointmentsCms = () => {
  const mutation = useMutation<any, Error, { pauseBetweenAppointmentsForCMS: boolean; pauseBetweenAppointmentsDurationForCMS: number }>({
    mutationFn: async ({ pauseBetweenAppointmentsForCMS, pauseBetweenAppointmentsDurationForCMS }) => {
      const response: AxiosResponse<any> = await httpClient.post(
        pauseBetweenAppointmentsCmsEndpoint(), 
        { 
          pauseBetweenAppointmentsForCMS,
          pauseBetweenAppointmentsDurationForCMS:pauseBetweenAppointmentsForCMS ? pauseBetweenAppointmentsDurationForCMS : 0
        }
      );
      return response.data;
    },
    onError: (error) => {
      toast.error('Greška prilikom promjene opcije za CMS!');
    },
    onSuccess: () => {
      toast.success('CMS postavke spašene');
    }
  });
  return mutation;
};

export const useLocationTravelTime = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (locationTravelTimeEnabled: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        locationTravelTimeEndpoint(),
        { locationTravelTimeEnabled }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom promjene opcije lokacije termina!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useViberStaffNotifications = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (viberStaffNotifications: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        viberStaffNotificationsEndpoint(), 
        { viberStaffNotifications }
      );
      return response.data;
    },
    onError: (error) => {
      toast.error('Greška prilikom promjene Viber notifikacija za zaposlenike!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useUpdateViberEnabled = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (viberEnabled: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        viberEnabledEndpoint(),
        { viberEnabled }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom promjene Viber feature postavke!');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
      queryClient.invalidateQueries({ queryKey: ["activeBanners"] });
      queryClient.invalidateQueries({ queryKey: ["viberQuota"] });
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useShowAppointmentPrice = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (showAppointmentPriceToClient: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        showAppointmentPriceEndpoint(),
        { showAppointmentPriceToClient }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom promjene vidljivosti cijene!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useShowAppointmentDuration = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (showAppointmentDurationToClient: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        showAppointmentDurationEndpoint(),
        { showAppointmentDurationToClient }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom promjene vidljivosti trajanja!');
    },
    onSuccess: () => {
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useUpdateOnlineReservationDays = () => {
  const mutation = useMutation<any, Error, string>({
    mutationFn: async (onlineReservationDays: string) => {
      const response: AxiosResponse<any> = await httpClient.post(
        onlineReservationDaysEndpoint(),
        { onlineReservationDays }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom spremanja dostupnih dana!');
    },
  });
  return mutation;
};

export const useUpdateClientCancellationWindowHours = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<any, Error, number>({
    mutationFn: async (clientCancellationWindowHours: number) => {
      const response: AxiosResponse<any> = await httpClient.post(
        clientCancellationWindowHoursEndpoint(),
        { clientCancellationWindowHours }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom promjene roka za otkazivanje termina!');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
      toast.success('Spašeno');
    }
  });
  return mutation;
};

export const useUpdateOnlineReservationTimePermanent = () => {
  const mutation = useMutation<any, Error, boolean>({
    mutationFn: async (onlineReservationTimePermanent: boolean) => {
      const response: AxiosResponse<any> = await httpClient.post(
        onlineReservationTimePermanentEndpoint(),
        { onlineReservationTimePermanent }
      );
      return response.data;
    },
    onError: () => {
      toast.error('Greška prilikom spremanja postavki!');
    },
  });
  return mutation;
};